import { useState } from "react";
import { Plus, Phone, Mail, CalendarClock, Frown as HorseIcon } from "lucide-react";
import { PROSPECT_STATUS } from "@/lib/prospectApi";
import { INVITE_STAGE } from "@/lib/inviteApi";
import { fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { ProspectDialog } from "./ProspectDialog";

export function Pill({ map, k, testId }) {
  const [l, c] = map[k] || [k, "bg-stone-50 text-stone-600 border-stone-200"];
  return <span data-testid={testId} className={`inline-flex items-center h-6 px-2 rounded-full border text-[11px] font-semibold ${c}`}>{l}</span>;
}

function Row({ p, i, onOpen }) {
  return (
    <li>
      <button type="button" data-testid={`prospect-row-${i}`} onClick={onOpen}
        className="w-full text-left px-3 sm:px-4 py-3 min-h-[64px] hover:bg-stone-50 active:bg-stone-100 transition-colors">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-semibold text-stone-900 break-words">{p.contact_name}</span>
          <Pill map={PROSPECT_STATUS} k={p.status} testId={`prospect-status-${i}`} />
          {p.invitation && <Pill map={INVITE_STAGE} k={p.invitation.stage} testId={`prospect-invite-stage-${i}`} />}
        </div>
        <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-stone-500">
          {p.horse_name && <span className="inline-flex items-center gap-1"><HorseIcon className="h-3.5 w-3.5 text-amber-800" />{p.horse_name}</span>}
          {p.phone && <span className="inline-flex items-center gap-1"><Phone className="h-3.5 w-3.5" />{p.phone}</span>}
          {p.email && <span className="inline-flex items-center gap-1 break-all"><Mail className="h-3.5 w-3.5" />{p.email}</span>}
          {p.follow_up_date && <span className="inline-flex items-center gap-1 text-amber-800 font-medium"><CalendarClock className="h-3.5 w-3.5" />Follow up {fmtDate(p.follow_up_date)}</span>}
        </div>
        {p.notes?.length > 0 && <p className="mt-1 text-xs text-stone-600 line-clamp-1">{p.notes[p.notes.length - 1].text}</p>}
      </button>
    </li>
  );
}

export function ProspectsPanel({ rows, onChange, isTest = false }) {
  const [open, setOpen] = useState(null);
  const current = open && open !== "new" ? rows.find((r) => r.id === open) : null;
  return (
    <section data-testid="prospects-panel" className="mb-4 md:mb-6">
      <div className="flex items-center justify-between gap-3 mb-2">
        <h2 className="font-display text-base md:text-lg font-bold text-stone-900">Prospects <span className="text-stone-400 font-normal text-sm">({rows.length})</span></h2>
        <Button data-testid="add-prospect-button" onClick={() => setOpen("new")} className="h-11 rounded-full bg-amber-800 hover:bg-amber-900 text-white px-4">
          <Plus className="h-4 w-4 mr-1.5" />Add Prospect
        </Button>
      </div>
      {rows.length ? (
        <ul data-testid="prospects-list" className="bg-white border border-stone-200 rounded-xl shadow-sm divide-y divide-stone-100 overflow-hidden">
          {rows.map((p, i) => <Row key={p.id} p={p} i={i} onOpen={() => setOpen(p.id)} />)}
        </ul>
      ) : <p data-testid="prospects-empty" className="text-sm text-stone-500 bg-white border border-stone-200 rounded-xl p-4">No prospects yet. Tap “Add Prospect” to record a new lead.</p>}
      <ProspectDialog open={!!open} prospect={current} extra={{ is_test: isTest }} onClose={() => setOpen(null)} onSaved={(p) => { onChange(p); if (open === "new") setOpen(p.id); }} />
    </section>
  );
}
